import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import styled from 'styled-components';

import Text from '@/components/Text';
import useHasPermission from '@/hooks/useHasPermission';
import { FreelancerWageResource } from '@/types/resources';

import { BoxHeadingBrake, BoxHeadingRow, EditButton, NoDetailsText } from '..';
import FreelancerWageRates from './FreelancerWageRates';
import FreelancerWageType from './FreelancerWageType';
import FreelancerWagesModal from './FreelancerWagesModal';

const FreelancerNoDetailsBox = styled.div`
  margin-bottom: 16px;
  justify-content: center;
  align-items: center;
  min-height: 182px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  padding: 24px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  display: flex;
`;

const Box = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  padding: 24px;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
`;

const Number = styled(Text)`
  margin-right: 8px;
  height: 24px;
  width: 48px;
  background: ${({ theme }) => theme.colors.lightGray};
  border-radius: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
`;

const WagesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grayscale};
  padding: 24px 0;
  &:first-child {
    padding-top: 0;
  }
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

interface Props {
  index: number;
  departmentsCount: number;
  wages?: FreelancerWageResource[];
  departmentId: number;
}

const FreelancerWages: FC<Props> = ({
  departmentsCount,
  index,
  wages,
  departmentId,
}) => {
  const [showModal, setShowModal] = useState(false);
  const userCanManageData = useHasPermission('manage freelancers');
  return (
    <>
      <BoxHeadingRow>
        <Heading>
          {departmentsCount > 1 && (
            <Number $type="smallBold">Nr. {index}</Number>
          )}
          <Text $color="gray" $type="smallHeadline">
            WAGES DETAILS
          </Text>
        </Heading>
        <BoxHeadingBrake />
        {userCanManageData && (
          <EditButton
            small
            color="secondary"
            onClick={() => setShowModal(true)}
          >
            Add
          </EditButton>
        )}
      </BoxHeadingRow>
      {!!wages?.length ? (
        <Box>
          <WagesRow>
            <FreelancerWageType type="hour" wages={wages} />
            <FreelancerWageRates type="hour" wages={wages} />
          </WagesRow>
          <WagesRow>
            <FreelancerWageType type="day" wages={wages} />
            <FreelancerWageRates type="day" wages={wages} />
          </WagesRow>
          <WagesRow>
            <FreelancerWageType type="month" wages={wages} />
            <FreelancerWageRates type="month" wages={wages} />
          </WagesRow>
          <WagesRow>
            <FreelancerWageType type="job" wages={wages} />
            <FreelancerWageRates type="job" wages={wages} />
          </WagesRow>
          <WagesRow>
            <FreelancerWageType type="other" wages={wages} />
            <FreelancerWageRates type="other" wages={wages} />
          </WagesRow>
        </Box>
      ) : (
        <FreelancerNoDetailsBox>
          <NoDetailsText $type="caption" $color="gray">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.40625 6.66675H13.3322"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.40625 9.6297H13.3322"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.40625 12.5927H10.3692"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.023 18.5185H2.95949C2.76303 18.5185 2.57462 18.4369 2.43571 18.2917C2.29679 18.1464 2.21875 17.9495 2.21875 17.7441V2.25586C2.21875 2.05047 2.29679 1.8535 2.43571 1.70827C2.57462 1.56303 2.76303 1.48145 2.95949 1.48145H17.7743C17.9708 1.48145 18.1592 1.56303 18.2981 1.70827C18.437 1.8535 18.515 2.05047 18.515 2.25586V12.7768C18.515 12.8785 18.4959 12.9792 18.4587 13.0732C18.4214 13.1671 18.3669 13.2525 18.2981 13.3244L13.5468 18.2917C13.478 18.3636 13.3964 18.4206 13.3065 18.4595C13.2166 18.4985 13.1203 18.5185 13.023 18.5185V18.5185Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18.4463 12.5918H13.3281V17.7103"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            No details added
          </NoDetailsText>
        </FreelancerNoDetailsBox>
      )}
      <AnimatePresence>
        {userCanManageData && showModal && (
          <FreelancerWagesModal
            departmentId={departmentId}
            onClose={() => setShowModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default FreelancerWages;
